import React, { useMemo, useState, useEffect } from 'react';
import { NavLink, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import { getPublicImageUrl } from '../../utils/storage';
import * as FiIcons from 'react-icons/fi';

const {
  FiHome, FiCalendar, FiUsers, FiBarChart3,
  FiCreditCard, FiSettings, FiLogOut, FiUser, FiShield,
  FiMenu, FiX,   FiShare2, FiMail, FiSliders, FiClipboard
} = FiIcons;

const Sidebar = () => {
  const { user, profile, role, displayName, logout } = useAuth();
  const navigate = useNavigate();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const debugAuth = import.meta.env.VITE_DEBUG_AUTH === 'true';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = useMemo(() => {
    const dashboardPath = role === 'student' ? '/dashboard/student-dashboard' : '/dashboard';
    const baseItems = [
      { path: dashboardPath, icon: FiHome, label: 'Dashboard' }
    ];

    if (role === 'agent' || role === 'admin') {
      baseItems.push(
        { path: '/dashboard/events', icon: FiCalendar, label: 'My Events' },
        { path: '/presentations', icon: FiSliders, label: 'Presentations' },
        { path: '/dashboard/leads', icon: FiUsers, label: 'Leads' },
        { path: '/dashboard/surveys', icon: FiClipboard, label: 'Surveys' },
        { path: '/dashboard/analytics', icon: FiBarChart3, label: 'Analytics' },
        { path: '/dashboard/sharing-links', icon: FiShare2, label: 'Invitation Links' },
      );
    }

    if (role === 'student') {
      baseItems.push(
        { path: '/dashboard/my-events', icon: FiCalendar, label: 'My Events' }
      );
    }

    if (role === 'admin') {
      baseItems.push(
        { path: '/dashboard/admin', icon: FiShield, label: 'Admin Panel' },
        { path: '/marketing', icon: FiMail, label: 'Marketing' }
      );
    }

    baseItems.push(
      { path: '/dashboard/payments', icon: FiCreditCard, label: 'Payments' }
    );

    return baseItems;
  }, [role]);

  useEffect(() => {
    if (!debugAuth) {
      return;
    }

    console.groupCollapsed('[Sidebar] navigation render');
    console.debug('role', role ?? null);
    console.debug('menuItems', navItems.map(({ path, label }) => ({ path, label })));
    console.groupEnd();
  }, [debugAuth, role, navItems]);

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 bg-slate-800 text-white p-2 rounded-lg shadow-lg"
      >
        <SafeIcon icon={isMobileOpen ? FiX : FiMenu} className="w-6 h-6" />
      </button>

      {/* Overlay */}
      {isMobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`${
        isMobileOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-40 w-72 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 border-r border-slate-700/50 flex flex-col transition-transform duration-300`}>
        
        {/* Logo Section */}
        <div className="p-6 border-b border-slate-700/50">
          <div className="flex items-center space-x-3 mb-2">
            <div className="relative">
              <div className="w-12 h-12 bg-gradient-to-br from-[#00AAFF] via-[#0088dd] to-[#1B4C87] rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-white font-bold text-xl">PL</span>
              </div>
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-400 rounded-full border-2 border-slate-900 animate-pulse"></div>
            </div>
            <div>
              <h1 className="text-lg font-bold text-white leading-none" style={{ fontFamily: 'Dancing Script, cursive' }}>
                Prosperity Leaders
              </h1>
              <p className="text-xs text-slate-400 mt-0.5">Events Platform</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item, index) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => setIsMobileOpen(false)}
              className={({ isActive }) =>
                `flex items-center space-x-3 px-4 py-3.5 rounded-xl transition-all group ${
                  isActive
                    ? 'bg-gradient-to-r from-[#00AAFF]/20 to-emerald-500/20 text-white border-l-4 border-[#00AAFF] shadow-lg'
                    : 'text-slate-300 hover:bg-slate-700/50 hover:text-white'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <div className={`${isActive ? 'text-[#00AAFF]' : 'text-slate-400 group-hover:text-[#00AAFF]'} transition-colors`}>
                    <SafeIcon icon={item.icon} className="w-5 h-5" />
                  </div>
                  <span className="font-medium">{item.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-slate-700/50">
          <Link
            to="/dashboard/profile"
            className="block bg-slate-700/30 rounded-xl p-4 mb-3 hover:bg-slate-700/50 transition-colors cursor-pointer group"
          >
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg group-hover:ring-2 group-hover:ring-[#00AAFF]/50 overflow-hidden bg-gradient-to-br from-[#00AAFF] to-emerald-400">
                {getPublicImageUrl(profile?.profile_image_url, 'profile-images') ? (
                  <img src={getPublicImageUrl(profile.profile_image_url, 'profile-images')} alt="" className="w-full h-full object-cover" />
                ) : (
                  <SafeIcon icon={FiUser} className="w-5 h-5 text-white" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white truncate group-hover:text-[#00AAFF]/90">{displayName}</p>
                <p className="text-xs text-slate-400 capitalize">{role}</p>
              </div>
            </div>
          </Link>
          
          <button
            onClick={handleLogout}
            className="flex items-center space-x-3 px-4 py-3 w-full text-left text-slate-300 hover:bg-slate-700/50 hover:text-white rounded-xl transition-all group"
          >
            <SafeIcon icon={FiLogOut} className="w-5 h-5 text-slate-400 group-hover:text-red-400 transition-colors" />
            <span className="font-medium">Logout</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
